import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import type { GoogleUser } from '../types/auth';
import { useTask } from '../context/TaskContext';
import { useFeatures } from '../context/FeatureFlagContext';
import { normalizePrStatus, type NormalizedPrStatus } from '../utils/taskStatus';

const PR_STATUS_STYLES: Record<NormalizedPrStatus, { label: string; color: string; background: string; border: string }> = {
    open: { label: 'Open', color: '#1a7f37', background: '#dafbe1', border: '#1f883d' },
    closed: { label: 'Closed', color: '#cf222e', background: '#ffebe9', border: '#a40e26' },
    merged: { label: 'Merged', color: '#8250df', background: '#e8ddff', border: '#8250df' },
    draft: { label: 'Draft', color: '#57606a', background: '#f6f8fa', border: '#afb8c1' },
};

function PRStatusBadge({ status }: { status?: string }) {
    const normalized = normalizePrStatus(status);
    if (!normalized) {
        return null;
    }
    const style = PR_STATUS_STYLES[normalized];
    return (
        <span
            style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '4px',
                padding: '2px 6px',
                borderRadius: '999px',
                backgroundColor: style.background,
                border: `1px solid ${style.border}`,
                color: style.color,
                fontWeight: 600,
                fontSize: '12px',
                lineHeight: 1.2,
            }}
        >
            <span
                aria-hidden="true"
                style={{
                    width: '8px',
                    height: '8px',
                    borderRadius: '50%',
                    backgroundColor: style.color,
                }}
            />
            <span>{style.label}</span>
        </span>
    );
}

interface UserMenuProps {
    user?: GoogleUser;
}

export default function UserMenu({ user }: Readonly<UserMenuProps>) {
    const [menuOpen, setMenuOpen] = useState(false);
    const [prMenuOpen, setPrMenuOpen] = useState(false);
    const menuRef = useRef<HTMLDivElement>(null);
    const prMenuRef = useRef<HTMLDivElement>(null);
    const navigate = useNavigate();
    const location = useLocation();
    const features = useFeatures();
    const headerTitle = features['TestTitle'] ? 'I like cheese burgers' : 'Plan 42';
    const { prLinks, prStatuses } = useTask();
    const prEntries = useMemo(
        () =>
            Object.entries(prLinks ?? {}).sort(([a], [b]) =>
                a.localeCompare(b)
            ),
        [prLinks]
    );
    const hasPRLinks = prEntries.length > 0;
    const showPrDropdown = prEntries.length > 1;
    const singlePrLink = prEntries[0]?.[1];

    useEffect(() => {
        function handleClickOutside(e: MouseEvent) {
            if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
                setMenuOpen(false);
            }
        }
        if (menuOpen) {
            document.addEventListener('click', handleClickOutside);
        }
        return () => document.removeEventListener('click', handleClickOutside);
    }, [menuOpen]);

    useEffect(() => {
        function handleClickOutside(e: MouseEvent) {
            if (prMenuRef.current && !prMenuRef.current.contains(e.target as Node)) {
                setPrMenuOpen(false);
            }
        }
        if (prMenuOpen) {
            document.addEventListener('click', handleClickOutside);
        }
        return () => document.removeEventListener('click', handleClickOutside);
    }, [prMenuOpen]);

    useEffect(() => {
        if (!showPrDropdown) {
            setPrMenuOpen(false);
        }
    }, [showPrDropdown]);

    useEffect(() => {
        setPrMenuOpen(false);
    }, [prLinks]);

    const path = location.pathname;
    const fromState = (location.state as { from?: string } | null)?.from;
    let backPath: string | null = null;
    const wsTurnMatch = path.match(/^\/workstreams\/tasks\/([^/]+)\/([^/]+)\/turns\//);
    const wsTaskMatch = !wsTurnMatch && path.match(/^\/workstreams\/tasks\/([^/]+)\/([^/]+)$/);
    const wsShortMatch = !wsTurnMatch && !wsTaskMatch && path.match(/^\/workstreams\/tasks\/([^/]+)$/);
    if (wsTurnMatch) {
        // Turn/logs page → back to the workstream task page.
        backPath = `/workstreams/tasks/${wsTurnMatch[1]}/${wsTurnMatch[2]}`;
    } else if (wsTaskMatch) {
        // Workstream task page → back to the workstream detail page.
        backPath = `/workstreams/${wsTaskMatch[1]}`;
    } else if (wsShortMatch) {
        // Short-name task page → back to workstreams list.
        backPath = '/workstreams';
    } else if (path.startsWith('/tasks/')) {
        backPath = path === '/tasks/new' ? null : '/';
    } else if (path === '/workstreams') {
        backPath = '/tasks/new';
    } else if (path.startsWith('/workstreams/')) {
        // Workstream detail page → back to workstreams list.
        backPath = '/workstreams';
    } else if (path === '/environments') {
        backPath = (fromState && fromState !== '/environments') ? fromState : '/tasks/new';
    } else if (path === '/environments/new') {
        backPath = '/environments';
    } else if (path === '/settings/github') {
        backPath = (fromState && fromState !== '/settings/github') ? fromState : '/tasks/new';
    } else {
        const envMatch = path.match(/^\/environments\/([^/]+)(?:\/edit)?$/);
        if (envMatch) {
            const id = envMatch[1];
            backPath = path.endsWith('/edit') ? `/environments/${id}` : '/environments';
        }
    }
    const showBack = backPath !== null;

    useEffect(() => {
        document.title = headerTitle;
    }, [headerTitle]);

    return (
        <div
            ref={menuRef}
            style={{
                position: 'sticky',
                top: 0,
                left: 0,
                width: '100%',
                boxSizing: 'border-box',
                padding: '10px',
                paddingRight: 'calc(10px + (100vw - 100%))',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                borderBottom: '1px solid #ccc',
                background: '#fff',
                zIndex: 1000,
                minHeight: '52px',
            }}
        >
            <div>
                {showBack && (
                    <button
                        onClick={() => backPath && navigate(backPath)}
                        style={{
                            fontSize: '32px',
                            border: 'none',
                            background: 'transparent',
                            cursor: 'pointer',
                            color: '#24292f',
                            padding: '0 6px',
                            lineHeight: 0,
                        }}
                    >
                        {/* Back arrow icon (arrow with shaft, pointing left) */}
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        >
                            {/* shaft */}
                            <line x1="19" y1="12" x2="5" y2="12" />
                            {/* arrowhead */}
                            <polyline points="12 5 5 12 12 19" />
                        </svg>
                    </button>
                )}
            </div>
            <div style={{ flex: 1, textAlign: 'center', fontWeight: 'bold' }}>
                <Link to="/tasks/new" style={{ color: 'inherit', textDecoration: 'none' }}>
                    {headerTitle}
                </Link>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                <Link className="menu-item-link" to="/workstreams">
                    Workstreams
                </Link>
                <Link className="menu-item-link" to="/environments" state={{ from: location.pathname }}>
                    Environments
                </Link>
                {hasPRLinks && (
                    showPrDropdown ? (
                        <div ref={prMenuRef} style={{ position: 'relative' }}>
                            <button
                                type="button"
                                className="pill-button btn-blue"
                                onClick={() => setPrMenuOpen((open) => !open)}
                            >
                                <svg
                                    aria-hidden="true"
                                    width="20"
                                    height="20"
                                    viewBox="0 0 16 16"
                                    fill="currentColor"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <path d="M8 0C3.58 0 0 3.58 0 8c0 3.54 2.29 6.53 5.47 7.59.4.07.55-.17.55-.38 0-.19-.01-.82-.01-1.49-2.01.37-2.53-.49-2.69-.94-.09-.23-.48-.94-.82-1.13-.28-.15-.68-.52-.01-.53.63-.01 1.08.58 1.23.82.72 1.21 1.87.87 2.33.66.07-.52.28-.87.51-1.07-1.78-.2-3.64-.89-3.64-3.95 0-.87.31-1.59.82-2.15-.08-.2-.36-1.02.08-2.12 0 0 .67-.21 2.2.82a7.62 7.62 0 0 1 2-.27c.68 0 1.36.09 2 .27 1.53-1.04 2.2-.82 2.2-.82.44 1.1.16 1.92.08 2.12.51.56.82 1.27.82 2.15 0 3.07-1.87 3.75-3.65 3.95.29.25.5.61.5 1.25 0 .9-.01 1.63-.01 1.85 0 .21.15.46.55.38A8.013 8.013 0 0 0 16 8c0-4.42-3.58-8-8-8Z" />
                                </svg>
                                <span>View PRs</span>
                                <svg
                                    aria-hidden="true"
                                    width="16"
                                    height="16"
                                    viewBox="0 0 16 16"
                                    fill="currentColor"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <path d="M4.646 6.646a.5.5 0 0 1 .708 0L8 9.293l2.646-2.647a.5.5 0 0 1 .708.708l-3 3a.5.5 0 0 1-.708 0l-3-3a.5.5 0 0 1 0-.708z" />
                                </svg>
                            </button>
                            {prMenuOpen && (
                                <div
                                    style={{
                                        position: 'absolute',
                                        top: 'calc(100% + 4px)',
                                        right: 0,
                                        background: '#fff',
                                        border: '1px solid #ccc',
                                        borderRadius: '12px',
                                        boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                                        minWidth: '220px',
                                        overflow: 'hidden',
                                        zIndex: 1000,
                                    }}
                                >
                                    {prEntries.map(([repo, link], index) => {
                                        const status = prStatuses?.[repo];
                                        return (
                                        <a
                                            key={repo}
                                            href={link}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            style={{
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                alignItems: 'center',
                                                gap: '12px',
                                                padding: '10px 14px',
                                                textDecoration: 'none',
                                                color: '#1f2933',
                                                fontSize: '14px',
                                                background: '#fff',
                                                borderTop: index === 0 ? 'none' : '1px solid #eee',
                                            }}
                                        >
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', flex: 1 }}>
                                                <span
                                                    style={{
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        whiteSpace: 'nowrap',
                                                    }}
                                                    title={repo}
                                                >
                                                    {repo}
                                                </span>
                                                <PRStatusBadge status={status} />
                                            </div>
                                            <svg
                                                aria-hidden="true"
                                                width="16"
                                                height="16"
                                                viewBox="0 0 24 24"
                                                fill="none"
                                                stroke="currentColor"
                                                strokeWidth="2"
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                            >
                                                <path d="M18 13v6a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                                                <polyline points="15 3 21 3 21 9" />
                                                <line x1="10" y1="14" x2="21" y2="3" />
                                            </svg>
                                        </a>
                                    );
                                    })}
                                </div>
                            )}
                        </div>
                    ) : (
                        <a
                            href={singlePrLink ?? '#'}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="pill-button pill-link-button"
                        >
                            <svg
                                aria-hidden="true"
                                width="20"
                                height="20"
                                viewBox="0 0 16 16"
                                fill="currentColor"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <path d="M8 0C3.58 0 0 3.58 0 8c0 3.54 2.29 6.53 5.47 7.59.4.07.55-.17.55-.38 0-.19-.01-.82-.01-1.49-2.01.37-2.53-.49-2.69-.94-.09-.23-.48-.94-.82-1.13-.28-.15-.68-.52-.01-.53.63-.01 1.08.58 1.23.82.72 1.21 1.87.87 2.33.66.07-.52.28-.87.51-1.07-1.78-.2-3.64-.89-3.64-3.95 0-.87.31-1.59.82-2.15-.08-.2-.36-1.02.08-2.12 0 0 .67-.21 2.2.82a7.62 7.62 0 0 1 2-.27c.68 0 1.36.09 2 .27 1.53-1.04 2.2-.82 2.2-.82.44 1.1.16 1.92.08 2.12.51.56.82 1.27.82 2.15 0 3.07-1.87 3.75-3.65 3.95.29.25.5.61.5 1.25 0 .9-.01 1.63-.01 1.85 0 .21.15.46.55.38A8.013 8.013 0 0 0 16 8c0-4.42-3.58-8-8-8Z" />
                            </svg>
                            <span>View PR</span>
                        </a>
                    )
                )}
                {user?.picture && (
                    <img
                        src={user.picture}
                        alt="profile"
                        onClick={() => setMenuOpen((o) => !o)}
                        style={{
                            width: '32px',
                            height: '32px',
                            borderRadius: '50%',
                            cursor: 'pointer',
                            display: 'block',
                        }}
                    />
                )}
            </div>
            {menuOpen && (
                <div
                    style={{
                        position: 'absolute',
                        top: 'calc(100% + 4px)',
                        right: 0,
                        background: '#fff',
                        border: '1px solid #ccc',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                        zIndex: 100,
                        minWidth: '160px',
                        padding: '4px 0',
                    }}
                >
                    <Link
                        className="menu-item-link"
                        to="/settings/github"
                        state={{ from: location.pathname }}
                        style={{
                            display: 'block',
                            padding: '6px 12px',
                        }}
                        onClick={() => setMenuOpen(false)}
                    >
                        Configure Github
                    </Link>
                    <a
                        href="/logout"
                        className="menu-item-link"
                        style={{ display: 'block', padding: '6px 12px' }}
                        onClick={() => setMenuOpen(false)}
                    >
                        Sign Out
                    </a>
                </div>
            )}
        </div>
    );
}
